/**
 * How a lead card's holds are written, and the two halves of what they say.
 *
 * "17+" is the seventeenth hold plus a controlled move out of it: worth more than
 * a plain 17 and less than an 18, which is why the server stores it as 17.5 (see
 * `LeadRoundResult`). The wire and the whole of the UI speak the "+" notation; the
 * half only exists in the database and in the ranking.
 *
 * Apart from `HoldsCountInput` so the lint's `react-refresh/only-export-components`
 * rule gets a component file that exports components only — the same split the
 * climb-standing icon has.
 */

/** A holds count as the wire carries it, split into the two things it says. */
export interface Holds {
  /** The hold reached, as typed. Empty while nothing has been entered. */
  count: string;
  /** Whether a controlled move was made out of it — the "+". */
  plus: boolean;
}

/** Reads "17+" into its two halves. Anything unparseable reads as nothing. */
export function parseHolds(value: string | null | undefined): Holds {
  const text = (value ?? '').toString().trim();
  if (text === '') return { count: '', plus: false };

  const plus = text.endsWith('+');
  const count = (plus ? text.slice(0, -1) : text).trim();

  return { count: /^\d+$/.test(count) ? count : '', plus };
}

/** And back: `17` + plus is `"17+"`. Nothing typed is nothing at all. */
export function formatHolds({ count, plus }: Holds): string {
  if (count === '') return '';
  return plus ? `${count}+` : count;
}

/**
 * The number the ranking compares: 17.5 for "17+". Null when there is no card,
 * which is what a route somebody did not climb is worth.
 */
export function holdsToNumber(value: string | null | undefined): number | null {
  const { count, plus } = parseHolds(value);
  if (count === '') return null;

  return Number(count) + (plus ? 0.5 : 0);
}
